import { el, tel, StatusLine } from "./dom.ts";
import { t } from "./i18n.ts";
import { getState, subscribe } from "./state.ts";
import { moduleIconSvg } from "./icons.ts";
import { formatModuleCount } from "./module-units.ts";
import { MODULES } from "../modules/registry.ts";
import type { ScanNode } from "../scan/tree.ts";
import { collectIds } from "../scan/tree.ts";
import type { ExportPlan, ContainerSelection, DateRange } from "../export/plan.ts";
import type { ViewerSource } from "./viewer/source.ts";
import type { ModuleId } from "../auth/config.ts";

/**
 * One zone per item type (ARCHITECTURE.md §15). Each module gets a single card
 * holding everything about it: how much is already on disk (with "Open" to
 * browse it), All/None, and — for a module chosen for export — a checkbox tree
 * of what to include, scanned on demand. Teams and Mail also carry their own
 * date filter.
 *
 * The scan runs one module at a time with visible per-module progress, and the
 * tree is built in yielding chunks, so a large account (a deep OneDrive, many
 * chats) never locks the page up while it renders.
 *
 * Scan and browse-source are injected, so the whole thing is driveable by
 * fakes in a harness without a live Graph/disk connection.
 */

/** Only these two offer a date filter (individually). */
const DATED_MODULES = new Set<ModuleId>(["teams", "mail"]);

/** Only these two can be deleted from Microsoft (Graph supports it). */
const DELETABLE = new Set<ModuleId>(["teams", "mail"]);

/** Build this many tree rows, then yield to the browser to keep it responsive. */
const RENDER_CHUNK = 150;

const yieldToBrowser = (): Promise<void> => new Promise((resolve) => setTimeout(resolve, 0));

interface Zone {
    moduleId: ModuleId;
    treeArea: HTMLElement;
    countEl: HTMLElement;
    openButton: HTMLButtonElement;
    /** Present only for types that support deleting from Microsoft (Teams, Mail). */
    deleteButton?: HTMLButtonElement;
    allButton: HTMLButtonElement;
    noneButton: HTMLButtonElement;
    afterInput?: HTMLInputElement;
    beforeInput?: HTMLInputElement;
    checked: Set<string>;
    checkboxes: Map<string, HTMLInputElement>;
    descendants: Map<string, string[]>;
    allIds: string[];
    scanned: boolean;
    count: number;
}

export interface ScanSection {
    readonly root: HTMLElement;
    getPlan(): ExportPlan;
    scan(): Promise<void>;
    /** Re-read on-disk browse counts (call after an export finishes). */
    refresh(): Promise<void>;
    wasScanned(): boolean;
}

export function createScanSection(deps: {
    runScan: (moduleId: ModuleId, signal?: AbortSignal) => Promise<ScanNode[]>;
    source: ViewerSource;
    openViewer: (moduleId: ModuleId) => void;
    /** Permanently delete the selected containers of a type from Microsoft. */
    deleteFromMicrosoft: (moduleId: ModuleId, containerIds: readonly string[]) => Promise<void>;
}): ScanSection {
    const status = new StatusLine();
    const scanButton = tel("button", "scan.run", { type: "button", class: "primary" });
    const progressBarFill = el("div", { class: "progress-bar-fill" });
    const progressBar = el("div", { class: "progress-bar scan-progress" }, [progressBarFill]);
    progressBar.hidden = true;
    const modulesWrap = el("div", { class: "scan-modules" });

    const root = el("div", { class: "scan-section" }, [
        el("div", { class: "scan-topbar" }, [el("div", { class: "actions" }, [scanButton]), status.node]),
        progressBar,
        modulesWrap
    ]);

    const zones = new Map<ModuleId, Zone>();

    /* ---- tree rendering ------------------------------------------------- */

    const refreshTriState = (z: Zone): void => {
        for (const [id, descendants] of z.descendants) {
            if (descendants.length === 0) {
                continue;
            }
            const box = z.checkboxes.get(id);
            if (!box) {
                continue;
            }
            const checkedCount = descendants.filter((d) => z.checked.has(d)).length;
            box.checked = checkedCount === descendants.length;
            box.indeterminate = checkedCount > 0 && checkedCount < descendants.length;
            if (box.checked) {
                z.checked.add(id);
            } else {
                z.checked.delete(id);
            }
        }
        if (z.deleteButton) {
            z.deleteButton.disabled = !z.scanned || z.checked.size === 0;
        }
    };

    const nodeRow = (node: ScanNode, z: Zone, depth: number): HTMLElement => {
        const box = el("input", { type: "checkbox", class: "scan-check" }) as HTMLInputElement;
        box.checked = z.checked.has(node.id);
        z.checkboxes.set(node.id, box);
        box.addEventListener("change", () => {
            for (const id of [node.id, ...(z.descendants.get(node.id) ?? [])]) {
                const child = z.checkboxes.get(id);
                if (box.checked) {
                    z.checked.add(id);
                } else {
                    z.checked.delete(id);
                }
                if (child) {
                    child.checked = box.checked;
                    child.indeterminate = false;
                }
            }
            refreshTriState(z);
        });
        return el("label", { class: "scan-node", style: `padding-left:${depth * 1.2}rem` }, [
            box,
            el("span", { class: "scan-node-label" }, [node.label]),
            ...(node.hint ? [el("span", { class: "scan-node-hint" }, [node.hint])] : [])
        ]);
    };

    /** Async + chunked so a large tree paints progressively instead of freezing. */
    const renderTree = async (z: Zone, nodes: readonly ScanNode[]): Promise<void> => {
        z.checkboxes.clear();
        z.descendants.clear();
        z.allIds = collectIds(nodes);
        z.checked = new Set(z.allIds);

        const flat: { node: ScanNode; depth: number }[] = [];
        const walk = (list: readonly ScanNode[], depth: number): void => {
            for (const node of list) {
                z.descendants.set(node.id, collectIds(node.children));
                flat.push({ node, depth });
                walk(node.children, depth + 1);
            }
        };
        walk(nodes, 0);

        const tree = el("div", { class: "scan-tree" });
        z.treeArea.replaceChildren(tree);

        if (flat.length === 0) {
            tree.append(el("p", { class: "scan-hint" }, [t("scan.emptyModule")]));
            z.scanned = true;
            return;
        }

        for (let i = 0; i < flat.length; i += 1) {
            const entry = flat[i];
            if (!entry) {
                continue;
            }
            tree.append(nodeRow(entry.node, z, entry.depth));
            if ((i + 1) % RENDER_CHUNK === 0) {
                await yieldToBrowser();
            }
        }

        z.scanned = true;
        z.allButton.hidden = false;
        z.noneButton.hidden = false;
        refreshTriState(z);
    };

    /* ---- zones ---------------------------------------------------------- */

    for (const definition of MODULES) {
        const icon = el("span", { class: "module-card-icon" });
        icon.innerHTML = moduleIconSvg(definition.id);

        const countEl = el("span", { class: "scan-count" }, [t("browse.notExported")]);
        const openButton = tel("button", "browse.open", { type: "button", class: "scan-open" });
        openButton.disabled = true;
        openButton.addEventListener("click", () => deps.openViewer(definition.id));

        // Deleting from Microsoft is offered for Teams and Mail only (Graph
        // supports it there); disabled until a scan gives a selection to act on.
        const deletable = DELETABLE.has(definition.id);
        const deleteButton = deletable
            ? tel("button", "scan.delete", { type: "button", class: "scan-delete" })
            : undefined;
        if (deleteButton) {
            deleteButton.disabled = true;
        }

        const allButton = tel("button", "scan.selectAll", { type: "button", class: "scan-all" });
        const noneButton = tel("button", "scan.selectNone", { type: "button", class: "scan-all" });
        allButton.hidden = true;
        noneButton.hidden = true;

        const treeArea = el("div", { class: "scan-tree-area" }, [
            el("p", { class: "scan-hint" }, [t("scan.notScanned")])
        ]);

        const zone: Zone = {
            moduleId: definition.id,
            treeArea,
            countEl,
            openButton,
            ...(deleteButton ? { deleteButton } : {}),
            allButton,
            noneButton,
            checked: new Set(),
            checkboxes: new Map(),
            descendants: new Map(),
            allIds: [],
            scanned: false,
            count: 0
        };

        const bodyChildren: (Node | string)[] = [];
        if (DATED_MODULES.has(definition.id)) {
            const afterInput = el("input", {
                type: "date",
                class: "date-input",
                id: `scan-after-${definition.id}`
            }) as HTMLInputElement;
            const beforeInput = el("input", {
                type: "date",
                class: "date-input",
                id: `scan-before-${definition.id}`
            }) as HTMLInputElement;
            zone.afterInput = afterInput;
            zone.beforeInput = beforeInput;
            bodyChildren.push(
                el("div", { class: "date-filter zone-date" }, [
                    tel("label", "scan.dateAfter", { for: afterInput.id }),
                    afterInput,
                    tel("label", "scan.dateBefore", { for: beforeInput.id }),
                    beforeInput
                ])
            );
        }
        bodyChildren.push(treeArea);

        zones.set(definition.id, zone);

        allButton.addEventListener("click", () => {
            zone.checked = new Set(zone.allIds);
            for (const box of zone.checkboxes.values()) {
                box.checked = true;
                box.indeterminate = false;
            }
            refreshTriState(zone);
        });
        noneButton.addEventListener("click", () => {
            zone.checked.clear();
            for (const box of zone.checkboxes.values()) {
                box.checked = false;
                box.indeterminate = false;
            }
            refreshTriState(zone);
        });

        // The typed-DELETE confirmation and the actual deletion live in
        // export-panel's deleteFromMicrosoft; here we just hand it the checked
        // container ids (chats / folders) for this type.
        deleteButton?.addEventListener("click", () => {
            deleteButton.disabled = true;
            void deps.deleteFromMicrosoft(zone.moduleId, [...zone.checked]).finally(() => {
                deleteButton.disabled = !zone.scanned || zone.checked.size === 0;
            });
        });

        modulesWrap.append(
            el("div", { class: "scan-module" }, [
                el("div", { class: "scan-module-head" }, [
                    icon,
                    el("span", { class: "scan-module-name" }, [t(definition.labelKey)]),
                    countEl,
                    el("span", { class: "scan-module-actions" }, [
                        allButton,
                        noneButton,
                        openButton,
                        ...(deleteButton ? [deleteButton] : [])
                    ])
                ]),
                el("div", { class: "scan-module-body" }, bodyChildren)
            ])
        );
    }

    /* ---- selectable state + browse counts ------------------------------ */

    const applySelectable = (): void => {
        const selected = new Set(getState().selectedModules);
        for (const [id, zone] of zones) {
            if (zone.scanned) {
                continue;
            }
            zone.treeArea.replaceChildren(
                el("p", { class: "scan-hint" }, [
                    selected.has(id) ? t("scan.notScanned") : t("scan.moduleNotSelected")
                ])
            );
        }
    };

    const renderCounts = (): void => {
        for (const zone of zones.values()) {
            zone.countEl.textContent =
                zone.count > 0 ? formatModuleCount(zone.count, zone.moduleId) : t("browse.notExported");
            zone.openButton.disabled = zone.count === 0;
        }
    };

    const refresh = async (): Promise<void> => {
        const hasFolder = getState().folderHandle !== null;
        for (const zone of zones.values()) {
            let count = 0;
            if (hasFolder) {
                try {
                    const data = await deps.source.loadIndex(zone.moduleId);
                    count = data?.rows.length ?? 0;
                } catch {
                    count = 0;
                }
            }
            zone.count = count;
        }
        renderCounts();
    };

    /* ---- scan ----------------------------------------------------------- */

    const scan = async (): Promise<void> => {
        const selected = getState().selectedModules;
        if (selected.length === 0) {
            status.set(t("scan.noModules"), "warn");
            return;
        }
        scanButton.disabled = true;
        progressBar.hidden = false;
        progressBarFill.style.width = "0%";

        let done = 0;
        try {
            for (const moduleId of selected) {
                const zone = zones.get(moduleId);
                const label = t(MODULES.find((m) => m.id === moduleId)?.labelKey ?? "app.title");
                status.set(t("scan.scanningModule", { module: label, done: done + 1, total: selected.length }), "muted");
                if (zone) {
                    zone.treeArea.replaceChildren(el("p", { class: "scan-hint" }, [t("scan.scanning")]));
                }
                // Yield first so the "scanning" state paints before the request.
                await yieldToBrowser();
                const nodes = await deps.runScan(moduleId);
                if (zone) {
                    await renderTree(zone, nodes);
                }
                done += 1;
                progressBarFill.style.width = `${Math.round((done / selected.length) * 100)}%`;
            }
            status.set(t("scan.done"), "ok");
        } catch (err) {
            status.set(t("scan.error", { message: err instanceof Error ? err.message : String(err) }), "err");
        } finally {
            scanButton.disabled = false;
            progressBar.hidden = true;
        }
    };

    scanButton.addEventListener("click", () => void scan());

    const getPlan = (): ExportPlan => {
        const selection: ExportPlan["selection"] = {};
        const dateRanges: ExportPlan["dateRanges"] = {};

        for (const [moduleId, zone] of zones) {
            if (zone.scanned) {
                if (zone.checked.size >= zone.allIds.length) {
                    selection[moduleId] = "all";
                } else {
                    const sel: ContainerSelection = { ids: [...zone.checked] };
                    selection[moduleId] = sel;
                }
            }
            if (zone.afterInput || zone.beforeInput) {
                const range: DateRange = {};
                if (zone.afterInput?.value) {
                    range.after = zone.afterInput.value;
                }
                if (zone.beforeInput?.value) {
                    range.before = zone.beforeInput.value;
                }
                dateRanges[moduleId] = range;
            }
        }
        return { selection, dateRanges };
    };

    const wasScanned = (): boolean => {
        for (const zone of zones.values()) {
            if (zone.scanned) {
                return true;
            }
        }
        return false;
    };

    subscribe(() => {
        applySelectable();
        void refresh();
    });
    applySelectable();
    void refresh();

    return { root, getPlan, scan, refresh, wasScanned };
}
